import { Color } from './java/Color';
import { Font, FontMetrics, Graphics2D, RenderingHints } from './java/Graphics2D';
import { Rectangle } from './java/Geom';
import { Random } from './java/Random';
import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { ID } from './ID';
import { Game } from './Game';
import { EnemyBossBullet } from './EnemyBossBullet';
import { PageRenderer } from './PageRenderer';

export class EnemyBoss extends GameObject {

  private static readonly SIZE = 96;
  private static readonly R = 16;

  private handler: Handler;
  private r: Random = new Random();
  private pulsePhase = 0;
  private readonly boundsRect: Rectangle = new Rectangle();
  private static readonly EMPTY_BOUNDS: Rectangle = new Rectangle(0, 0, 0, 0);

  // Entry animation — syncs with boss intro cinematic
  private entryTimer = 100; // matches Game.bossIntroTimer crash point
  private warmupTimer = 30;

  // Health — drains over time (survival-based), boss dies when empty
  private maxHp: number;
  private hp: number;
  private defeated = false;
  private deathTimer = 0;

  // Attack system
  private attackCooldown = 0;
  private attackPattern = 0;
  private patternStep = 0;
  private burstCount = 0;

  // Phase thresholds
  private static readonly PHASE2_THRESHOLD = 0.6;
  private static readonly PHASE3_THRESHOLD = 0.25;

  // Colors per phase
  private static readonly FILL_P1: Color = new Color(235, 87, 87);
  private static readonly FILL_P2: Color = new Color(220, 60, 120);
  private static readonly FILL_P3: Color = new Color(180, 40, 180);
  private static readonly BAR_BG: Color = new Color(22, 30, 44);
  private static readonly BAR_BORDER: Color = new Color(40, 52, 70);

  private static readonly FONT_BOSS: Font = new Font('Arial', Font.BOLD, 14);
  private static readonly FONT_PHASE: Font = new Font('Arial', Font.BOLD, 11);

  constructor(x: number, y: number, id: ID, handler: Handler) {
    super(x, y, id);
    this.handler = handler;
    this.velX = 0;
    this.velY = 2;

    // HP scales with game difficulty
    this.maxHp = 1200;
    this.hp = this.maxHp;
  }

  setMaxHp(hp: number): void {
    this.maxHp = hp;
    this.hp = hp;
  }

  isDefeated(): boolean {
    return this.defeated && this.deathTimer > 60;
  }

  takeDamage(dmg: number): boolean {
    if (this.defeated) return false;
    this.hp -= dmg;
    if (this.hp < 0) this.hp = 0;
    return true;
  }

  getHpPercent(): number {
    return this.hp / this.maxHp;
  }

  getPhase(): number {
    const pct = this.hp / this.maxHp;
    if (pct <= EnemyBoss.PHASE3_THRESHOLD) return 3;
    if (pct <= EnemyBoss.PHASE2_THRESHOLD) return 2;
    return 1;
  }

  getBounds(): Rectangle {
    if (this.defeated || this.entryTimer > 0) return EnemyBoss.EMPTY_BOUNDS;
    this.boundsRect.x = Math.trunc(this.x);
    this.boundsRect.y = Math.trunc(this.y);
    this.boundsRect.width = EnemyBoss.SIZE;
    this.boundsRect.height = EnemyBoss.SIZE;
    return this.boundsRect;
  }

  tick(): void {
    if (this.defeated) {
      this.deathTimer++;
      return;
    }

    this.x += this.velX;
    this.y += this.velY;
    this.pulsePhase += 0.08;

    // Entry animation
    if (this.entryTimer > 0) {
      this.entryTimer--;
      if (this.entryTimer <= 0) this.velY = 0;
      return;
    }
    if (this.warmupTimer > 0) {
      this.warmupTimer--;
      if (this.warmupTimer <= 0 && this.velX === 0) this.velX = 2;
      return;
    }

    // HP drains over time — survive to defeat the boss
    let drainRate = 0.8 + (1 - this.hp / this.maxHp) * 0.4; // drains faster as HP lowers
    if (Game.combatMode) drainRate *= 0.35; // player must actively deal damage
    this.hp -= drainRate;

    if (this.hp <= 0) {
      this.hp = 0;
      this.defeated = true;
      // Queue cascading bullet explosions instead of instant clear
      Game.triggerBulletCascade(
        this.x + Math.trunc(EnemyBoss.SIZE / 2),
        this.y + Math.trunc(EnemyBoss.SIZE / 2),
        this.handler,
      );
      return;
    }

    // Movement — speeds up in later phases
    const speedMult = this.getPhase() === 3 ? 1.5 : this.getPhase() === 2 ? 1.2 : 1;
    if (this.velX > 0) this.velX += 0.005 * speedMult;
    else if (this.velX < 0) this.velX -= 0.005 * speedMult;
    this.velX = Game.clamp(this.velX, -10 * speedMult, 10 * speedMult);

    if (this.x <= 0 || this.x >= Game.WIDTH - EnemyBoss.SIZE) {
      Game.wallHit(
        this.x <= 0 ? 0 : Game.WIDTH,
        this.y + Math.trunc(EnemyBoss.SIZE / 2),
        this.x <= 0 ? 2 : 3,
      );
      this.velX *= -1;
    }

    // Attack patterns
    this.attackCooldown--;
    if (this.attackCooldown <= 0) {
      this.executeAttack();
    }
  }

  private executeAttack(): void {
    const phase = this.getPhase();
    const cx = Math.trunc(this.x) + Math.trunc(EnemyBoss.SIZE / 2);
    const cy = Math.trunc(this.y) + EnemyBoss.SIZE;

    switch (this.attackPattern) {
      case 0: { // Spread shot
        const count = phase === 3 ? 9 : phase === 2 ? 7 : 5;
        const startAngle = -(count - 1) * 8;
        for (let i = 0; i < count; i++) {
          const angle = startAngle + i * 16;
          const vx = Math.sin(angle * Math.PI / 180) * 4;
          const vy = 4 + Math.abs(vx) * 0.3;
          this.handler.addObject(new EnemyBossBullet(cx, cy, ID.BasicEnemy, this.handler, vx, vy));
        }
        this.attackCooldown = phase === 3 ? 40 : phase === 2 ? 55 : 70;
        break;
      }

      case 1: { // Aimed burst at player
        const player = this.findPlayer();
        if (player !== null) {
          this.burstCount++;
          const dx = player.getX() + 24 - cx;
          const dy = player.getY() + 24 - cy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 0) {
            const speed = phase >= 2 ? 6 : 5;
            // Slight spread on each burst shot
            const spread = (this.burstCount - 2) * 6;
            const baseAngle = Math.atan2(dy, dx) * 180 / Math.PI;
            const a = (baseAngle + spread) * Math.PI / 180;
            this.handler.addObject(new EnemyBossBullet(cx, cy, ID.BasicEnemy, this.handler,
              Math.cos(a) * speed, Math.sin(a) * speed));
          }
          if (this.burstCount >= (phase >= 2 ? 5 : 3)) {
            this.burstCount = 0;
            this.attackCooldown = phase === 3 ? 50 : 65;
          } else {
            this.attackCooldown = 8; // fast between burst shots
          }
        } else {
          this.attackCooldown = 30;
        }
        break;
      }

      case 2: { // Wave — alternating left-right streams
        this.patternStep++;
        const leftSide = this.patternStep % 2 === 0;
        const wx = leftSide ? Math.trunc(this.x) + 10 : Math.trunc(this.x) + EnemyBoss.SIZE - 10;
        this.handler.addObject(new EnemyBossBullet(Math.trunc(wx), cy, ID.BasicEnemy, this.handler,
          leftSide ? -2 : 2, 5));
        if (this.patternStep >= (phase === 3 ? 16 : phase === 2 ? 12 : 8)) {
          this.patternStep = 0;
          this.attackCooldown = phase === 3 ? 35 : 50;
        } else {
          this.attackCooldown = 5;
        }
        break;
      }

      case 3: { // Ring burst (phase 2+)
        if (phase >= 2) {
          const ringCount = phase === 3 ? 16 : 12;
          for (let i = 0; i < ringCount; i++) {
            const a = (2 * Math.PI * i / ringCount) + this.pulsePhase;
            const speed = 3.5;
            this.handler.addObject(new EnemyBossBullet(cx, cy, ID.BasicEnemy, this.handler,
              Math.cos(a) * speed, Math.sin(a) * speed));
          }
          this.attackCooldown = phase === 3 ? 60 : 80;
        } else {
          this.attackCooldown = 10; // skip to next pattern in phase 1
        }
        break;
      }
    }

    // Cycle to next pattern when cooldown is set (not mid-burst)
    if (this.attackCooldown > 15) {
      this.attackPattern = (this.attackPattern + 1) % 4;
    }
  }

  private findPlayer(): GameObject | null {
    for (let i = 0; i < this.handler.getObjects().length; i++) {
      if (this.handler.getObjects()[i].getId() === ID.Player)
        return this.handler.getObjects()[i];
    }
    return null;
  }

  render(g: Graphics2D): void {
    const g2: Graphics2D = g;
    g2.setRenderingHint(RenderingHints.KEY_ANTIALIASING, RenderingHints.VALUE_ANTIALIAS_ON);
    const ix = Math.trunc(this.x), iy = Math.trunc(this.y);

    if (this.defeated) {
      // Death flash — shrinking, fading
      const t = Math.min(this.deathTimer / 60, 1);
      const shrink = Math.trunc(t * EnemyBoss.SIZE / 2);
      const alpha = Math.trunc((1 - t) * 255);
      if (alpha > 0) {
        g2.setColor(new Color(255, 255, 255, alpha));
        g2.fillRoundRect(ix + shrink, iy + shrink, EnemyBoss.SIZE - shrink * 2, EnemyBoss.SIZE - shrink * 2,
          EnemyBoss.R, EnemyBoss.R);
      }
      return;
    }

    // Phase-based color
    const fill: Color = this.getPhase() === 3 ? EnemyBoss.FILL_P3
      : this.getPhase() === 2 ? EnemyBoss.FILL_P2 : EnemyBoss.FILL_P1;

    const pulse = Math.sin(this.pulsePhase) * 0.5 + 0.5;
    const glowSize = 8 + Math.trunc(4 * pulse);
    g2.setColor(new Color(fill.getRed(), fill.getGreen(), fill.getBlue(), 15 + Math.trunc(15 * pulse)));
    g2.fillRoundRect(ix - glowSize, iy - glowSize, EnemyBoss.SIZE + glowSize * 2, EnemyBoss.SIZE + glowSize * 2,
      EnemyBoss.R + glowSize, EnemyBoss.R + glowSize);

    g2.setColor(fill);
    g2.fillRoundRect(ix, iy, EnemyBoss.SIZE, EnemyBoss.SIZE, EnemyBoss.R, EnemyBoss.R);

    g2.setColor(new Color(fill.getRed(), fill.getGreen(), fill.getBlue(), 100 + Math.trunc(80 * pulse)));
    g2.drawRoundRect(ix, iy, EnemyBoss.SIZE, EnemyBoss.SIZE, EnemyBoss.R, EnemyBoss.R);

    // Rotating inner geometry — two overlapping squares
    const bcx = ix + Math.trunc(EnemyBoss.SIZE / 2), bcy = iy + Math.trunc(EnemyBoss.SIZE / 2);
    const innerR = Math.trunc(EnemyBoss.SIZE / 3);
    g2.setColor(new Color(fill.getRed(), fill.getGreen(), fill.getBlue(), 50 + Math.trunc(30 * pulse)));
    this.drawRotatedSquare(g2, bcx, bcy, innerR, this.pulsePhase * 0.6);
    this.drawRotatedSquare(g2, bcx, bcy, innerR - 6, -this.pulsePhase * 0.4);

    // Orbiting dots
    const dotCount = 4 + this.getPhase();
    const orbitR = Math.trunc(EnemyBoss.SIZE / 2) - 8;
    g2.setColor(new Color(fill.getRed(), fill.getGreen(), fill.getBlue(), 80));
    for (let i = 0; i < dotCount; i++) {
      const a = this.pulsePhase * 0.5 + i * (Math.PI * 2 / dotCount);
      const dx = bcx + Math.trunc(Math.cos(a) * orbitR);
      const dy = bcy + Math.trunc(Math.sin(a) * orbitR);
      g2.fillOval(dx - 2, dy - 2, 4, 4);
    }

    // Boss health bar at top of screen
    this.renderBossBar(g2);
  }

  private drawRotatedSquare(g: Graphics2D, cx: number, cy: number, r: number, angle: number): void {
    const xp: number[] = new Array<number>(4).fill(0);
    const yp: number[] = new Array<number>(4).fill(0);
    for (let i = 0; i < 4; i++) {
      const a = angle + i * (Math.PI / 2);
      xp[i] = cx + Math.trunc(Math.cos(a) * r);
      yp[i] = cy + Math.trunc(Math.sin(a) * r);
    }
    g.drawPolygon(xp, yp, 4);
  }

  private renderBossBar(g: Graphics2D): void {
    const barW = 400;
    const barH = 10;
    const barX = Math.trunc((Game.WIDTH - barW) / 2);
    const barY = Game.HEIGHT - 40;
    const pct = this.hp / this.maxHp;

    // Background
    g.setColor(EnemyBoss.BAR_BG);
    g.fillRoundRect(barX, barY, barW, barH, 5, 5);

    // Fill — color changes with phase
    const fill: Color = this.getPhase() === 3 ? EnemyBoss.FILL_P3
      : this.getPhase() === 2 ? EnemyBoss.FILL_P2 : EnemyBoss.FILL_P1;
    const fillW = Math.trunc(barW * pct);
    if (fillW > 0) {
      g.setColor(fill);
      g.fillRoundRect(barX, barY, fillW, barH, 5, 5);
    }

    // Border
    g.setColor(EnemyBoss.BAR_BORDER);
    g.drawRoundRect(barX, barY, barW, barH, 5, 5);

    // Label
    g.setFont(EnemyBoss.FONT_BOSS);
    g.setColor(PageRenderer.TEXT_SEC);
    let fm: FontMetrics = g.getFontMetrics();
    const label = 'BOSS';
    g.drawString(label, barX - fm.stringWidth(label) - 10, barY + 9);

    // Phase indicator
    g.setFont(EnemyBoss.FONT_PHASE);
    g.setColor(fill);
    const phase = 'Phase ' + this.getPhase();
    fm = g.getFontMetrics();
    g.drawString(phase, barX + barW + 10, barY + 9);
  }
}
